import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, Trash2, ArrowUpRight, ArrowLeft, Loader2, FileText, Download, CheckCircle2, Clock,
} from "lucide-react";

const ROLES = ["Songwriter", "Producer", "Featured", "Composer", "Lyricist", "Beatmaker", "Other"];
const PROS = ["", "BMI", "ASCAP", "SESAC", "GMR", "PRS", "SOCAN", "GEMA", "SACEM"];

export default function SplitSheets() {
  const nav = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    song_title: "",
    alt_title: "",
    date_written: new Date().toISOString().slice(0, 10),
    session_location: "",
    notes: "",
    collaborators: [
      { _cid: "c0", name: "", email: "", role: "Songwriter", ipi: "", pro: "", publisher: "", split_pct: 100 },
    ],
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/split-sheets/my");
      setSheets(data);
    } catch (e) {
      console.error("Failed to load split sheets", e);
      toast.error("Could not load split sheets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addCollab = () => setForm((f) => ({
    ...f, collaborators: [...f.collaborators, { _cid: `c${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "", email: "", role: "Songwriter", ipi: "", pro: "", publisher: "", split_pct: 0 }],
  }));
  const removeCollab = (i) => setForm((f) => ({ ...f, collaborators: f.collaborators.filter((_, x) => x !== i) }));
  const patchCollab = (i, patch) => setForm((f) => ({
    ...f, collaborators: f.collaborators.map((c, x) => x === i ? { ...c, ...patch } : c),
  }));

  const totalPct = form.collaborators.reduce((s, c) => s + (parseFloat(c.split_pct) || 0), 0);
  const pctOk = Math.abs(totalPct - 100) < 0.01;

  const submit = async () => {
    if (!form.song_title.trim()) { toast.error("Song title is required."); return; }
    if (!pctOk) { toast.error(`Splits must total 100%. Currently ${totalPct}%.`); return; }
    for (const c of form.collaborators) {
      if (!c.name || !c.email) { toast.error("Every collaborator needs a name and email."); return; }
    }

    setCreating(true);
    try {
      const payload = {
        ...form,
        collaborators: form.collaborators.map(({ _cid, ...c }) => ({
          ...c,
          split_pct: parseFloat(c.split_pct) || 0,
        })),
      };
      const { data } = await api.post("/split-sheets", payload);
      toast.success(`Sheet created — ${data.collaborators} signing emails sent.`);
      setForm({
        song_title: "", alt_title: "", date_written: new Date().toISOString().slice(0, 10),
        session_location: "", notes: "",
        collaborators: [{ _cid: "c0", name: "", email: "", role: "Songwriter", ipi: "", pro: "", publisher: "", split_pct: 100 }],
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create sheet.");
    } finally {
      setCreating(false);
    }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/split-sheets/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `split-sheet-${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-16 md:py-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <button onClick={() => nav("/dashboard")} className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white mb-6 flex items-center gap-2">
          <ArrowLeft size={12} /> Back to dashboard
        </button>

        <div className="mb-10">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-3">
            // Split Sheets
          </div>
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
            Lock in your splits.<br />
            <span className="text-[var(--primary)]">Before the lawyers get involved.</span>
          </h1>
          <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl">
            Enter the song &amp; every collaborator. Each one gets an email with a one-click signing link. When all
            signatures are collected, download the fully-executed PDF and file it with your PRO.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Create form */}
          <div className="lg:col-span-7">
            <div className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8">
              <h2 className="font-heading font-black text-xl mb-6 tracking-tight">Create a new split sheet</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <F label="Song Title *" value={form.song_title} onChange={(v) => setForm({ ...form, song_title: v })} testid="ss-title" />
                <F label="Alt Title" value={form.alt_title} onChange={(v) => setForm({ ...form, alt_title: v })} testid="ss-alt" />
                <F label="Date Written" type="date" value={form.date_written} onChange={(v) => setForm({ ...form, date_written: v })} testid="ss-date" />
                <F label="Session Location" value={form.session_location} onChange={(v) => setForm({ ...form, session_location: v })} testid="ss-loc" />
              </div>

              {/* Collaborators */}
              <div className="mb-4 flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
                  Collaborators &amp; splits
                </div>
                <button type="button" onClick={addCollab} className="font-mono text-[10px] tracking-widest uppercase text-[var(--primary)] hover:text-white inline-flex items-center gap-1" data-testid="add-collab">
                  <Plus size={12} /> Add
                </button>
              </div>

              <div className="space-y-4" data-testid="collab-list">
                {form.collaborators.map((c, i) => (
                  <div key={c._cid} className="border border-white/10 p-4" data-testid={`collab-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">Collaborator #{i + 1}</div>
                      {form.collaborators.length > 1 && (
                        <button onClick={() => removeCollab(i)} className="text-zinc-500 hover:text-red-500" data-testid={`remove-collab-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <F label="Full Legal Name" value={c.name} onChange={(v) => patchCollab(i, { name: v })} testid={`collab-name-${i}`} />
                      <F label="Email" type="email" value={c.email} onChange={(v) => patchCollab(i, { email: v })} testid={`collab-email-${i}`} />
                      <div>
                        <Lbl>Role</Lbl>
                        <select value={c.role} onChange={(e) => patchCollab(i, { role: e.target.value })} className="w-full bg-black border border-white/15 text-white text-sm px-3 py-2" data-testid={`collab-role-${i}`}>
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <Lbl>Split % *</Lbl>
                        <input type="number" step="0.01" min="0" max="100" value={c.split_pct} onChange={(e) => patchCollab(i, { split_pct: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-2 text-white" data-testid={`collab-pct-${i}`} />
                      </div>
                      <div>
                        <Lbl>PRO</Lbl>
                        <select value={c.pro} onChange={(e) => patchCollab(i, { pro: e.target.value })} className="w-full bg-black border border-white/15 text-white text-sm px-3 py-2" data-testid={`collab-pro-${i}`}>
                          {PROS.map((p) => <option key={p} value={p}>{p || "—"}</option>)}
                        </select>
                      </div>
                      <F label="IPI / CAE" value={c.ipi} onChange={(v) => patchCollab(i, { ipi: v })} testid={`collab-ipi-${i}`} />
                      <div className="md:col-span-2">
                        <F label="Publisher Entity" value={c.publisher} onChange={(v) => patchCollab(i, { publisher: v })} testid={`collab-pub-${i}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 border border-white/10 flex items-center justify-between">
                <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">Total splits</div>
                <div className={`font-heading font-black text-2xl tracking-tight ${pctOk ? "text-[#22c55e]" : "text-red-400"}`} data-testid="total-pct">
                  {totalPct.toFixed(2)}%
                </div>
              </div>

              <div className="mt-6">
                <Lbl>Notes (optional)</Lbl>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full bg-transparent border border-white/15 focus:border-[var(--primary)] outline-none p-3 text-white text-sm" placeholder="Anything else that matters about this session..." data-testid="ss-notes" />
              </div>

              <button
                onClick={submit}
                disabled={creating || !pctOk}
                className="btn-primary mt-8 w-full justify-center disabled:opacity-40"
                data-testid="submit-splitsheet"
              >
                {creating ? <><Loader2 size={16} className="animate-spin" /> Sending signature emails…</> : <>Create &amp; send for signatures <ArrowUpRight size={16} /></>}
              </button>
            </div>
          </div>

          {/* Existing sheets */}
          <div className="lg:col-span-5">
            <div className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8">
              <h2 className="font-heading font-black text-xl mb-6 tracking-tight">Your split sheets</h2>
              {loading ? (
                <Loader2 className="animate-spin text-[var(--primary)]" size={20} />
              ) : sheets.length === 0 ? (
                <p className="text-zinc-500 text-sm">No sheets yet. Create your first one.</p>
              ) : (
                <div className="space-y-3" data-testid="my-sheets">
                  {sheets.map((s) => (
                    <div key={s.id} className="border border-white/10 p-4 hover:border-[var(--primary)]/30 transition-colors" data-testid={`sheet-${s.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-heading font-bold text-white truncate">{s.song_title}</div>
                          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mt-1">
                            {s.signed_count}/{s.collaborators} signed · {s.created_at?.slice(0, 10)}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {s.status === "fully_signed" ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase text-[#22c55e]">
                              <CheckCircle2 size={12} /> Signed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest uppercase text-[#3b82f6]">
                              <Clock size={12} /> Pending
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex gap-3">
                        <button onClick={() => downloadPdf(s.id)} className="font-mono text-[10px] tracking-widest uppercase text-[var(--primary)] hover:text-white inline-flex items-center gap-1" data-testid={`dl-${s.id}`}>
                          <Download size={12} /> Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-2 text-white text-sm" />
    </div>
  );
}
function Lbl({ children }) {
  return <label className="block font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">{children}</label>;
}
